import { useState, useMemo, useRef, useEffect } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import { useQuery } from '@tanstack/react-query';
import { getFunnelData, type FunnelData, type FunnelDailyRow, type FunnelType } from '@/api/funnel';
import { getCampaigns } from '@/api/campaigns';
import { getApps } from '@/api/apps';
import type { Campaign, App } from '@/api/types';
import { formatChartDay } from '@/lib/datetime';

type DateRange = '7d' | '30d' | '90d' | 'custom';

const dateRangeOptions: { value: DateRange; label: string; days: number }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
  { value: 'custom', label: 'Custom', days: 0 },
];

function formatNumber(num: number): string {
  return num.toLocaleString('en-US');
}

function formatMoney(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// --- Dropdown component ---
interface DropdownOption {
  value: string;
  label: string;
}

function Dropdown({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-surface-muted bg-surface min-w-[120px] justify-between"
      >
        <span className="truncate">{selected?.label || placeholder}</span>
        <svg className="w-4 h-4 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-surface border border-border rounded-lg shadow-lg z-50 min-w-[160px] max-h-60 overflow-auto">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-muted ${
                o.value === value ? 'bg-surface-muted font-medium' : ''
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Funnel bar ---
function FunnelBar({ name, value, maxValue, prevValue }: {
  name: string;
  value: number;
  maxValue: number;
  prevValue: number | null;
}) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  const convRate = prevValue !== null && prevValue > 0
    ? ((value / prevValue) * 100).toFixed(1)
    : null;

  return (
    <div className="flex items-center gap-4 py-2">
      <div className="w-32 text-sm text-content-muted text-right shrink-0">{name}</div>
      <div className="flex-1 relative">
        <div className="h-8 bg-surface-muted rounded-md overflow-hidden">
          <div
            className="h-full bg-purple-500 rounded-md transition-all duration-500"
            style={{ width: `${Math.max(pct, 1)}%` }}
          />
        </div>
      </div>
      <div className="w-24 text-sm font-medium text-content text-right shrink-0">
        {formatNumber(value)}
      </div>
      <div className="w-16 text-xs text-content-subtle text-right shrink-0">
        {convRate !== null ? `${convRate}%` : ''}
      </div>
    </div>
  );
}

// --- Custom tooltip ---
function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-lg shadow-lg p-3 text-xs">
      <div className="font-medium text-content mb-1">{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-content-muted">{entry.name}:</span>
          <span className="font-medium">{formatNumber(entry.value as number)}</span>
        </div>
      ))}
    </div>
  );
}

// --- Conversion ratio card ---
function CrRatioCard({
  label,
  ratio,
  numerator,
  denominator,
  denominatorLabel,
  accent,
}: {
  label: string;
  ratio: number;
  numerator: number;
  denominator: number;
  denominatorLabel: string;
  accent: string;
}) {
  const hasData = denominator > 0;
  const pct = Math.min(ratio, 100);
  return (
    <div className="relative bg-surface rounded-xl border border-border p-5 overflow-hidden">
      <div className="flex items-baseline justify-between">
        <div className="text-sm text-content-subtle">{label}</div>
        {hasData && (
          <div className="text-xs text-content-subtle tabular-nums">
            {formatNumber(numerator)} / {formatNumber(denominator)}
          </div>
        )}
      </div>
      <div className="text-2xl font-semibold text-content mt-1 tabular-nums">
        {hasData ? `${ratio.toFixed(ratio >= 10 ? 1 : 2)}%` : '—'}
      </div>
      <div className="mt-3 h-1.5 bg-surface-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${accent} transition-all duration-500`}
          style={{ width: hasData ? `${Math.max(pct, 1)}%` : '0%' }}
        />
      </div>
      {!hasData && (
        <div className="text-xs text-content-subtle mt-2">No {denominatorLabel.toLowerCase()} in range</div>
      )}
    </div>
  );
}

// --- Inline conversion-rate cell (for the daily table) ---
function CrPct({ num, denom }: { num: number; denom: number }) {
  if (denom <= 0) return <span className="text-content-subtle tabular-nums">—</span>;
  const v = (num / denom) * 100;
  const color =
    v >= 20 ? 'text-success'
    : v >= 10 ? 'text-success'
    : v >= 5 ? 'text-warning'
    : 'text-content-subtle';
  return (
    <span className={`${color} font-medium tabular-nums`}>
      {v.toFixed(v >= 10 ? 1 : 2)}%
    </span>
  );
}

export function Funnel() {
  const themeColors = useThemeColors();
  const [funnelType, setFunnelType] = useState<FunnelType>('pwa');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [campaignId, setCampaignId] = useState('all');
  const [appId, setAppId] = useState('all');
  const [country, setCountry] = useState('all');
  const [platform, setPlatform] = useState('all');

  const { startDate, endDate } = useMemo(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    const days = dateRangeOptions.find((d) => d.value === dateRange)?.days || 30;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, [dateRange, customStartDate, customEndDate]);

  const filters = useMemo(() => ({
    start_date: startDate,
    end_date: endDate,
    campaign_id: campaignId !== 'all' ? campaignId : undefined,
    app_id: appId !== 'all' ? appId : undefined,
    country: country !== 'all' ? country : undefined,
    platform: platform !== 'all' ? platform : undefined,
    funnel_type: funnelType,
  }), [startDate, endDate, campaignId, appId, country, platform, funnelType]);

  const { data: funnelData, isLoading } = useQuery<FunnelData>({
    queryKey: ['funnel', filters],
    queryFn: () => getFunnelData(filters),
    staleTime: 30_000,
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: ['campaigns'],
    queryFn: getCampaigns,
    staleTime: 300_000,
  });

  const { data: apps } = useQuery<App[]>({
    queryKey: ['apps'],
    queryFn: getApps,
    staleTime: 300_000,
  });

  const campaignOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: 'All Campaigns' },
    ...(campaigns || [])
      .filter((c) => c.status !== 'completed')
      .map((c) => ({ value: c.id, label: c.name })),
  ], [campaigns]);

  const appOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: 'All Apps' },
    ...(apps || [])
      .filter((a) => a.status !== 'deleted')
      .map((a) => ({ value: a.id, label: a.name })),
  ], [apps]);

  const countryOptions: DropdownOption[] = [
    { value: 'all', label: 'All Countries' },
  ];

  const platformOptions: DropdownOption[] = [
    { value: 'all', label: 'All Platforms' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
  ];

  const steps = funnelData?.steps || [];
  const maxValue = steps.length > 0 ? Math.max(...steps.map((s) => s.value), 1) : 1;

  // Headline conversion ratios: install → reg, reg → dep, install → dep.
  // For referral funnel we substitute "delivered" for "installs" (it's the
  // closest analogue — the user actually reached the target app).
  const conv = useMemo(() => {
    const get = (name: string) => steps.find((s) => s.name === name)?.value || 0;
    const base = funnelType === 'referral' ? get('Delivered') : get('Installs');
    const regs = get('Registrations');
    const deps = get('Deposits');
    const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);
    return {
      baseLabel: funnelType === 'referral' ? 'Delivered' : 'Installs',
      base,
      regs,
      deps,
      i2r: pct(regs, base),
      r2d: pct(deps, regs),
      i2d: pct(deps, base),
    };
  }, [steps, funnelType]);

  // Chart keys depend on funnel type
  const chartKeys = useMemo(() => {
    if (funnelType === 'referral') {
      return ['Impressions', 'Clicks', 'Referrals', 'Delivered', 'Registrations', 'Deposits'];
    }
    return ['Impressions', 'Clicks', 'PWA Visits', 'Installs', 'Registrations', 'Deposits'];
  }, [funnelType]);

  // Chart data
  const chartData = useMemo(() => {
    return [...(funnelData?.daily || [])].sort((a, b) => a.date.localeCompare(b.date)).map((d: FunnelDailyRow) => {
      const base: Record<string, any> = {
        date: formatChartDay(d.date),
        Impressions: d.impressions,
        Clicks: d.clicks,
        Registrations: d.registrations,
        Deposits: d.deposits,
      };
      if (funnelType === 'referral') {
        base['Referrals'] = d.referrals || 0;
        base['Delivered'] = d.delivered || 0;
      } else {
        base['PWA Visits'] = d.pwa_visits || 0;
        base['Installs'] = d.installs || 0;
      }
      return base;
    });
  }, [funnelData, funnelType]);

  const COLORS = ['#8b5cf6', '#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Funnel</h1>
          <p className="text-sm text-content-subtle mt-1">Full conversion funnel from impressions to deposits</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Funnel type toggle */}
        <div className="flex bg-primary/10 rounded-lg p-0.5">
          {([['pwa', 'PWA'], ['referral', 'Referral']] as const).map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFunnelType(val)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                funnelType === val
                  ? 'bg-surface shadow-sm font-medium text-primary'
                  : 'text-primary hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-surface-muted" />

        <div className="flex bg-surface-muted rounded-lg p-0.5">
          {dateRangeOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDateRange(opt.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                dateRange === opt.value
                  ? 'bg-surface shadow-sm font-medium text-content'
                  : 'text-content-muted hover:text-content'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {dateRange === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-2.5 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <span className="text-content-subtle">&mdash;</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-2.5 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
        )}
        <Dropdown value={campaignId} options={campaignOptions} onChange={setCampaignId} placeholder="Campaign" />
        <Dropdown value={appId} options={appOptions} onChange={setAppId} placeholder="App" />
        <Dropdown value={country} options={countryOptions} onChange={setCountry} placeholder="Country" />
        <Dropdown value={platform} options={platformOptions} onChange={setPlatform} placeholder="Platform" />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* Revenue card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-surface rounded-xl border border-border p-5">
              <div className="text-sm text-content-subtle">Revenue</div>
              <div className="text-2xl font-semibold text-content mt-1">
                {formatMoney(funnelData?.revenue_cents || 0)}
              </div>
            </div>
            <div className="bg-surface rounded-xl border border-border p-5">
              <div className="text-sm text-content-subtle">Registrations</div>
              <div className="text-2xl font-semibold text-content mt-1">
                {formatNumber(conv.regs)}
              </div>
            </div>
            <div className="bg-surface rounded-xl border border-border p-5">
              <div className="text-sm text-content-subtle">Deposits</div>
              <div className="text-2xl font-semibold text-content mt-1">
                {formatNumber(conv.deps)}
              </div>
            </div>
          </div>

          {/* Conversion ratios */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CrRatioCard
              label={`${conv.baseLabel} → Reg`}
              ratio={conv.i2r}
              numerator={conv.regs}
              denominator={conv.base}
              denominatorLabel={conv.baseLabel}
              accent="from-purple-500 to-indigo-500"
            />
            <CrRatioCard
              label="Reg → Dep"
              ratio={conv.r2d}
              numerator={conv.deps}
              denominator={conv.regs}
              denominatorLabel="Registrations"
              accent="from-indigo-500 to-blue-500"
            />
            <CrRatioCard
              label={`${conv.baseLabel} → Dep`}
              ratio={conv.i2d}
              numerator={conv.deps}
              denominator={conv.base}
              denominatorLabel={conv.baseLabel}
              accent="from-blue-500 to-cyan-500"
            />
          </div>

          {/* Funnel visualization */}
          <div className="bg-surface rounded-xl border border-border p-6">
            <h2 className="text-lg font-medium text-content mb-4">Conversion Funnel</h2>
            <div className="space-y-1">
              {steps.map((step, i) => (
                <FunnelBar
                  key={step.name}
                  name={step.name}
                  value={step.value}
                  maxValue={maxValue}
                  prevValue={i > 0 ? steps[i - 1].value : null}
                />
              ))}
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bg-surface rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-content mb-4">Daily Breakdown</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <defs>
                      {chartKeys.map((key, i) => (
                        <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS[i]} stopOpacity={0.15} />
                          <stop offset="95%" stopColor={COLORS[i]} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.grid} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke={themeColors.axis} />
                    <YAxis tick={{ fontSize: 12 }} stroke={themeColors.axis} />
                    <Tooltip content={<CustomTooltip />} />
                    {chartKeys.map((key, i) => (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={COLORS[i]}
                        fill={`url(#grad-${key})`}
                        strokeWidth={2}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Daily table */}
          {(funnelData?.daily || []).length > 0 && (
            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              <div className="p-6 pb-3">
                <h2 className="text-lg font-medium text-content">Daily Data</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-t border-border">
                      <th className="px-4 py-3 text-left text-xs font-medium text-content-subtle uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Impr</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Clicks</th>
                      {funnelType === 'referral' ? (
                        <>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Referrals</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Delivered</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">PWA Visits</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Install Clk</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Installs</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">PWA Opens</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Regs</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Deps</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase" title="Install (or Delivered) → Registration">I→R</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase" title="Registration → Deposit">R→D</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase" title="Install (or Delivered) → Deposit">I→D</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-content-subtle uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {[...(funnelData?.daily || [])].reverse().map((row: FunnelDailyRow) => (
                      <tr key={row.date} className="hover:bg-surface-muted">
                        <td className="px-4 py-2.5 text-content">{row.date}</td>
                        <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.impressions)}</td>
                        <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.clicks)}</td>
                        {funnelType === 'referral' ? (
                          <>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.referrals || 0)}</td>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.delivered || 0)}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.pwa_visits || 0)}</td>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.install_clicks || 0)}</td>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.installs || 0)}</td>
                            <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.pwa_opens || 0)}</td>
                          </>
                        )}
                        <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.registrations)}</td>
                        <td className="px-4 py-2.5 text-right text-content-muted">{formatNumber(row.deposits)}</td>
                        {(() => {
                          const base = funnelType === 'referral' ? (row.delivered || 0) : (row.installs || 0);
                          return (
                            <>
                              <td className="px-4 py-2.5 text-right"><CrPct num={row.registrations} denom={base} /></td>
                              <td className="px-4 py-2.5 text-right"><CrPct num={row.deposits} denom={row.registrations} /></td>
                              <td className="px-4 py-2.5 text-right"><CrPct num={row.deposits} denom={base} /></td>
                            </>
                          );
                        })()}
                        <td className="px-4 py-2.5 text-right text-content-muted">{formatMoney(row.revenue_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
